import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { propertiesAPI, adminAPI } from '../services/api';
import axios from 'axios';
import { ChevronLeft, CheckCircle2, XCircle, Clock, Eye, FileText, User, CreditCard, Building2, X, ZoomIn, LogOut, ShieldCheck, AlertTriangle, Calendar as CalendarIcon, Users, RotateCcw } from 'lucide-react';
import AdminBookingsTab from '../components/AdminBookingsTab';
import AdminHostsTab from '../components/AdminHostsTab';
import AdminPropertyEditsTab from '../components/AdminPropertyEditsTab';
import AdminBlogsTab from '../components/AdminBlogsTab';
import SustainabilityReviewBlock from '../components/SustainabilityReviewBlock';
import PropertyPreviewDialog from '../components/PropertyPreviewDialog';
import AdminCleanupButton from '../components/AdminCleanupButton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// KYC Document Preview Component
const resolveUploadUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  // Normalize: ensure path has /api prefix for correct routing
  if (url.startsWith('/uploads/')) return `${BACKEND_URL}/api${url}`;
  return `${BACKEND_URL}${url}`;
};

const KYCDocumentPreview = ({ url, label, icon: Icon, bgColor, textColor }) => {
  const [showModal, setShowModal] = useState(false);
  const fullUrl = resolveUploadUrl(url);
  const isPdf = url.toLowerCase().endsWith('.pdf');

  return (
    <>
      <div 
        onClick={() => setShowModal(true)}
        className={`cursor-pointer group relative overflow-hidden rounded-lg border-2 border-dashed ${bgColor} hover:border-solid transition-all`}
      >
        {isPdf ? (
          <div className="h-24 flex flex-col items-center justify-center p-2">
            <FileText className={`h-8 w-8 ${textColor} mb-1`} />
            <span className={`text-xs ${textColor} font-medium`}>{label}</span>
            <span className="text-xs text-gray-500">PDF Document</span>
          </div>
        ) : (
          <div className="relative h-24">
            <img 
              src={fullUrl} 
              alt={label}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div className="hidden h-full flex-col items-center justify-center">
              <Icon className={`h-8 w-8 ${textColor}`} />
              <span className={`text-xs ${textColor} mt-1`}>{label}</span>
            </div>
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
              <ZoomIn className="h-6 w-6 text-white" />
            </div>
          </div>
        )}
        <div className={`absolute bottom-0 left-0 right-0 ${bgColor} py-1 px-2`}>
          <div className="flex items-center justify-center">
            <Icon className={`h-3 w-3 ${textColor} mr-1`} />
            <span className={`text-xs ${textColor} font-medium`}>{label}</span>
          </div>
        </div>
      </div>

      {/* Modal for full preview */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80" onClick={() => setShowModal(false)}>
          <div className="relative max-w-4xl max-h-[90vh] m-4" onClick={(e) => e.stopPropagation()}>
            <button 
              onClick={() => setShowModal(false)}
              className="absolute -top-10 right-0 text-white hover:text-gray-300 transition-colors"
            >
              <X className="h-8 w-8" />
            </button>
            <div className="bg-white rounded-lg overflow-hidden shadow-2xl">
              <div className={`${bgColor} py-2 px-4 flex items-center`}>
                <Icon className={`h-5 w-5 ${textColor} mr-2`} />
                <span className={`font-semibold ${textColor}`}>{label}</span>
              </div>
              {isPdf ? (
                <div className="p-8 text-center">
                  <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 mb-4">PDF Document</p>
                  <a 
                    href={fullUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className={`inline-flex items-center px-4 py-2 rounded-lg ${bgColor} ${textColor} font-medium hover:opacity-80 transition-opacity`}
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    Open PDF
                  </a>
                </div>
              ) : (
                <img 
                  src={fullUrl} 
                  alt={label}
                  className="max-w-full max-h-[70vh] object-contain"
                />
              )}
            </div>
            <a 
              href={fullUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="block text-center text-white text-sm mt-3 hover:underline"
            >
              Open in new tab
            </a>
          </div>
        </div>
      )}
    </>
  );
};

const AdminApprovalPage = () => {
  const navigate = useNavigate();
  const [pendingProperties, setPendingProperties] = useState([]);
  const [rejectedProperties, setRejectedProperties] = useState([]);
  const [approvedProperties, setApprovedProperties] = useState([]);
  const [activeTab, setActiveTab] = useState('pending');
  const [pendingEditsCount, setPendingEditsCount] = useState(0);
  const [pendingBlogsCount, setPendingBlogsCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [kycModalOpen, setKycModalOpen] = useState(false);
  const [selectedPropertyKyc, setSelectedPropertyKyc] = useState(null);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectPropertyId, setRejectPropertyId] = useState(null);
  const [rejectRemarks, setRejectRemarks] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewProperty, setPreviewProperty] = useState(null);

  const openPropertyPreview = (property) => {
    setPreviewProperty(property);
    setPreviewOpen(true);
  };

  const openKycModal = (property) => {
    setSelectedPropertyKyc(property);
    setKycModalOpen(true);
  };

  const openRejectModal = (propertyId) => {
    setRejectPropertyId(propertyId);
    setRejectRemarks('');
    setRejectModalOpen(true);
  };

  useEffect(() => {
    verifyAdminAuth();
  }, []);

  const verifyAdminAuth = async () => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    try {
      const response = await adminAPI.verify(token);
      if (response.success && response.valid) {
        setIsAuthenticated(true);
        fetchPendingProperties();
      } else {
        localStorage.removeItem('adminToken');
        navigate('/admin/login');
      }
    } catch (error) {
      localStorage.removeItem('adminToken');
      navigate('/admin/login');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    toast.success('Logged out successfully');
    navigate('/admin/login');
  };

  const fetchPendingProperties = async () => {
    try {
      setLoading(true);
      // Fetch pending, rejected, and approved properties
      const [pendingResponse, rejectedResponse, approvedResponse] = await Promise.all([
        propertiesAPI.getAll({ status: 'pending' }),
        propertiesAPI.getAll({ status: 'rejected' }),
        propertiesAPI.getAll({ status: 'approved' })
      ]);
      setPendingProperties(pendingResponse.data || []);
      setRejectedProperties(rejectedResponse.data || []);
      setApprovedProperties(approvedResponse.data || []);

      // Also fetch pending edits count for the tab badge (best-effort)
      try {
        const token = localStorage.getItem('adminToken');
        const [editsRes, blogsRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/admin/property-edits`, { params: { token } }),
          axios.get(`${BACKEND_URL}/api/admin/blogs`, { params: { token, status: 'pending' } }),
        ]);
        setPendingEditsCount((editsRes.data?.data || []).length);
        setPendingBlogsCount((blogsRes.data?.data || []).length);
      } catch (_e) {
        // silent — the tabs will refresh count when opened
      }
    } catch (error) {
      console.error('Error fetching properties:', error);
      toast.error('Failed to load properties');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (propertyId) => {
    try {
      setActionLoading(propertyId);
      const response = await axios.put(
        `${BACKEND_URL}/api/properties/${propertyId}/approve`
      );
      
      if (response.data.success) {
        toast.success('Property approved successfully!');
        // Move from pending to approved
        const approved = pendingProperties.find(p => (p._id || p.id) === propertyId);
        if (approved) {
          approved.status = 'approved';
          setApprovedProperties(prev => [approved, ...prev]);
        }
        setPendingProperties(prev => prev.filter(p => (p._id || p.id) !== propertyId));
      }
    } catch (error) {
      console.error('Error approving property:', error);
      toast.error('Failed to approve property');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async () => {
    if (!rejectRemarks.trim()) {
      toast.error('Please enter rejection remarks');
      return;
    }
    
    try {
      setActionLoading(rejectPropertyId);
      const response = await axios.put(
        `${BACKEND_URL}/api/properties/${rejectPropertyId}/reject`,
        { remarks: rejectRemarks.trim() }
      );
      
      if (response.data.success) {
        // Find the rejected property and add remarks to it
        const rejectedProperty = pendingProperties.find(p => (p._id || p.id) === rejectPropertyId);
        if (rejectedProperty) {
          rejectedProperty.rejection_remarks = rejectRemarks.trim();
          rejectedProperty.status = 'rejected';
          rejectedProperty.rejected_at = new Date().toISOString();
          setRejectedProperties(prev => [rejectedProperty, ...prev]);
        }
        
        toast.success(
          <div>
            <strong>Property rejected</strong>
            <p className="text-sm mt-1">Remarks: {rejectRemarks.trim().substring(0, 100)}...</p>
          </div>,
          { duration: 5000 }
        );
        
        // Remove from pending list
        setPendingProperties(prev => prev.filter(p => (p._id || p.id) !== rejectPropertyId));
        setRejectModalOpen(false);
        setRejectRemarks('');
        setRejectPropertyId(null);
      }
    } catch (error) {
      console.error('Error rejecting property:', error);
      toast.error('Failed to reject property');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRevertToPending = async (property) => {
    const pid = property._id || property.id;
    const reason = window.prompt(
      `Move "${property.title}" back to Pending for re-review?\n\nOptional note (visible to admin history):`,
      '',
    );
    // Cancel returns null; empty string means confirm with no note
    if (reason === null) return;
    try {
      setActionLoading(pid);
      const response = await axios.put(
        `${BACKEND_URL}/api/properties/${pid}/revert-to-pending`,
        { reason: reason.trim() || null },
      );
      if (response.data.success) {
        toast.success('Property moved back to Pending');
        const updated = { ...property, status: 'pending', is_approved: false };
        setPendingProperties((prev) => [updated, ...prev]);
        setApprovedProperties((prev) => prev.filter((p) => (p._id || p.id) !== pid));
        setRejectedProperties((prev) => prev.filter((p) => (p._id || p.id) !== pid));
      }
    } catch (error) {
      console.error('Error reverting property:', error);
      toast.error(error.response?.data?.detail || 'Failed to move property to Pending');
    } finally {
      setActionLoading(null);
    }
  };

  if (!isAuthenticated) {
    return null; // Will redirect to login
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
            className="mb-4"
          >
            <ChevronLeft className="h-4 w-4 mr-2" />
            Back to home
          </Button>
            <div className="flex items-center gap-2 mb-4">
              <Button
                variant="outline"
                onClick={() => {
                  // Impersonate the GeoStays system host so the standard property-registration
                  // form (with pre-verified PAN) can be reused.
                  const SYSTEM_HOST_ID = 'geostays-admin-host';
                  localStorage.setItem('hostId', SYSTEM_HOST_ID);
                  localStorage.setItem('hostToken', SYSTEM_HOST_ID);
                  localStorage.setItem('adminActingAsHost', '1');
                  toast.success('Registering as GeoStays Hospitality Pvt Ltd');
                  navigate('/list-property');
                }}
                className="border-[#7CB342] text-[#7CB342] hover:bg-emerald-50"
                data-testid="admin-register-company-property"
              >
                <Building2 className="h-4 w-4 mr-2" />
                Register Company Property
              </Button>
              <Button
                variant="outline"
                onClick={handleLogout}
                className="text-red-600 hover:bg-red-50"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
              <p className="text-gray-600 mt-1">Review and approve pending homestay registrations</p>
            </div>
            <div className="flex items-center gap-3">
              <Badge variant="secondary" className="text-lg px-4 py-2 bg-yellow-100 text-yellow-800">
                {pendingProperties.length} Pending
              </Badge>
              <Badge variant="secondary" className="text-lg px-4 py-2 bg-green-100 text-green-800">
                {approvedProperties.length} Approved
              </Badge>
              <Badge variant="secondary" className="text-lg px-4 py-2 bg-red-100 text-red-800">
                {rejectedProperties.length} Rejected
              </Badge>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mt-6 border-b">
            <button
              onClick={() => setActiveTab('pending')}
              className={`px-6 py-3 font-medium transition-colors ${
                activeTab === 'pending'
                  ? 'border-b-2 border-[#7CB342] text-[#7CB342]'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Pending Approvals ({pendingProperties.length})
            </button>
            <button
              onClick={() => setActiveTab('approved')}
              className={`px-6 py-3 font-medium transition-colors ${
                activeTab === 'approved'
                  ? 'border-b-2 border-green-600 text-green-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Approved ({approvedProperties.length})
            </button>
            <button
              onClick={() => setActiveTab('rejected')}
              className={`px-6 py-3 font-medium transition-colors ${
                activeTab === 'rejected'
                  ? 'border-b-2 border-red-500 text-red-500'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Rejected ({rejectedProperties.length})
            </button>
            <button
              onClick={() => setActiveTab('property-edits')}
              className={`px-6 py-3 font-medium transition-colors flex items-center gap-2 ${
                activeTab === 'property-edits'
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              data-testid="admin-property-edits-tab-trigger"
            >
              Property Edits{pendingEditsCount > 0 ? ` (${pendingEditsCount})` : ''}
            </button>
            <button
              onClick={() => setActiveTab('blogs')}
              className={`px-6 py-3 font-medium transition-colors flex items-center gap-2 ${
                activeTab === 'blogs'
                  ? 'border-b-2 border-emerald-600 text-emerald-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              data-testid="admin-blogs-tab-trigger"
            >
              Blogs{pendingBlogsCount > 0 ? ` (${pendingBlogsCount})` : ''}
            </button>
            <button
              onClick={() => setActiveTab('bookings')}
              className={`px-6 py-3 font-medium transition-colors flex items-center gap-2 ${
                activeTab === 'bookings'
                  ? 'border-b-2 border-[#7CB342] text-[#7CB342]'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              data-testid="admin-bookings-tab-trigger"
            >
              <CalendarIcon className="h-4 w-4" />
              Bookings
            </button>
            <button
              onClick={() => setActiveTab('hosts')}
              className={`px-6 py-3 font-medium transition-colors flex items-center gap-2 ${
                activeTab === 'hosts'
                  ? 'border-b-2 border-[#7CB342] text-[#7CB342]'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              data-testid="admin-hosts-tab-trigger"
            >
              <Users className="h-4 w-4" />
              Hosts
            </button>
          </div>
        </div>

        {/* Loading State */}
        {activeTab === 'bookings' ? (
          <AdminBookingsTab />
        ) : activeTab === 'hosts' ? (
          <AdminHostsTab />
        ) : activeTab === 'property-edits' ? (
          <AdminPropertyEditsTab onCountChange={setPendingEditsCount} />
        ) : activeTab === 'blogs' ? (
          <AdminBlogsTab onCountChange={setPendingBlogsCount} />
        ) : loading ? (
          <div className="text-center py-20">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#7CB342]"></div>
            <p className="mt-4 text-gray-600">Loading properties...</p>
          </div>
        ) : activeTab === 'pending' ? (
          /* Pending Properties Tab */
          pendingProperties.length === 0 ? (
            <Card>
              <CardContent className="py-20 text-center">
                <Clock className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No Pending Properties</h3>
                <p className="text-gray-600">All homestays have been reviewed!</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {pendingProperties.map((property) => {
              const propertyId = property._id || property.id;
              const isProcessing = actionLoading === propertyId;

              return (
                <Card key={propertyId} className="overflow-hidden hover:shadow-lg transition-shadow">
                  <CardHeader className="bg-yellow-50 border-b">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <CardTitle className="text-xl">{property.title}</CardTitle>
                      <div className="flex items-center gap-2 flex-wrap">
                        {property.reverted_at && (
                          <Badge
                            variant="outline"
                            className="bg-orange-50 text-orange-800 border-orange-300 text-xs"
                            title={property.revert_reason ? `Reason: ${property.revert_reason}` : 'Moved back to Pending by admin'}
                            data-testid={`reverted-badge-${propertyId}`}
                          >
                            <RotateCcw className="h-3 w-3 mr-1" />
                            Reverted from {property.previous_status || 'approved'} on{' '}
                            {new Date(property.reverted_at).toLocaleDateString(undefined, {
                              day: 'numeric', month: 'short', year: 'numeric',
                            })}
                          </Badge>
                        )}
                        <Badge variant="outline" className="bg-yellow-100 text-yellow-800 border-yellow-300">
                          <Clock className="h-3 w-3 mr-1" />
                          Pending Review
                        </Badge>
                      </div>
                    </div>
                    {property.reverted_at && property.revert_reason && (
                      <p className="text-xs text-orange-700 mt-2" data-testid={`reverted-note-${propertyId}`}>
                        <strong>Admin note:</strong> {property.revert_reason}
                      </p>
                    )}
                  </CardHeader>
                  <CardContent className="p-6">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                      {/* Property Image */}
                      <div className="lg:col-span-1">
                        <img
                          src={resolveUploadUrl(property.images?.[0])}
                          alt={property.title}
                          className="w-full h-48 object-cover rounded-lg"
                          onError={(e) => {
                            e.target.src = 'https://images.unsplash.com/photo-1518780664697-55e3ad937233?w=400';
                          }}
                        />
                      </div>

                      {/* Property Details */}
                      <div className="lg:col-span-2 space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-sm text-gray-500">Location</p>
                            <p className="font-semibold">{property.location}, {property.country}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">Category</p>
                            <p className="font-semibold">{property.category}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">Price per Night</p>
                            <p className="font-semibold">₹{property.price.toLocaleString()}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">Capacity</p>
                            <p className="font-semibold">{property.guests} guests · {property.bedrooms} bedrooms</p>
                          </div>
                        </div>

                        <div>
                          <p className="text-sm text-gray-500 mb-1">Description</p>
                          <p className="text-sm text-gray-700 line-clamp-2">{property.description}</p>
                        </div>

                        <div>
                          <p className="text-sm text-gray-500 mb-2">Amenities</p>
                          <div className="flex flex-wrap gap-2">
                            {property.amenities.slice(0, 5).map((amenity, index) => (
                              <Badge key={index} variant="secondary" className="text-xs">
                                {amenity}
                              </Badge>
                            ))}
                            {property.amenities.length > 5 && (
                              <Badge variant="secondary" className="text-xs">
                                +{property.amenities.length - 5} more
                              </Badge>
                            )}
                          </div>
                        </div>

                        <div className="pt-4 border-t">
                          <p className="text-sm text-gray-500 mb-1">Host</p>
                          <div className="flex items-center space-x-3">
                            <img
                              src={property.host.image}
                              alt={property.host.name}
                              className="h-10 w-10 rounded-full"
                            />
                            <div>
                              <p className="font-semibold text-sm">{property.host.name}</p>
                              <p className="text-xs text-gray-500">Joined {property.host.joinedYear}</p>
                            </div>
                          </div>
                          {property.hostEmail && (
                            <p className="text-xs text-gray-500 mt-2">Email: {property.hostEmail} | Phone: {property.hostPhone}</p>
                          )}
                        </div>

                        {/* KYC Documents Section */}
                        {property.kyc_documents && Object.keys(property.kyc_documents).length > 0 && (
                          <div className="pt-4 border-t">
                            <p className="text-sm text-gray-500 mb-3">KYC Documents (Click to preview)</p>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                              {property.kyc_documents.hostPhoto && (
                                <KYCDocumentPreview 
                                  url={property.kyc_documents.hostPhoto}
                                  label="Photo"
                                  icon={User}
                                  bgColor="bg-blue-50"
                                  textColor="text-blue-700"
                                />
                              )}
                              {property.kyc_documents.aadhaarCard && (
                                <KYCDocumentPreview 
                                  url={property.kyc_documents.aadhaarCard}
                                  label="Aadhaar"
                                  icon={FileText}
                                  bgColor="bg-green-50"
                                  textColor="text-green-700"
                                />
                              )}
                              {property.kyc_documents.panCard && (
                                <KYCDocumentPreview 
                                  url={property.kyc_documents.panCard}
                                  label="PAN Card"
                                  icon={CreditCard}
                                  bgColor="bg-orange-50"
                                  textColor="text-orange-700"
                                />
                              )}
                              {property.kyc_documents.bankCheque && (
                                <KYCDocumentPreview 
                                  url={property.kyc_documents.bankCheque}
                                  label="Bank Cheque"
                                  icon={Building2}
                                  bgColor="bg-purple-50"
                                  textColor="text-purple-700"
                                />
                              )}
                            </div>
                          </div>
                        )}

                        {/* Bank Details Section */}
                        {property.bank_details && property.bank_details.accountName && (
                          <div className="pt-4 border-t">
                            <p className="text-sm text-gray-500 mb-2">Bank Details</p>
                            <div className="text-xs text-gray-700 space-y-1 bg-gray-50 p-3 rounded-lg">
                              <p><span className="font-medium">Account Holder:</span> {property.bank_details.accountName}</p>
                              <p><span className="font-medium">Account No:</span> {property.bank_details.accountNumber}</p>
                              <p><span className="font-medium">IFSC:</span> {property.bank_details.ifscCode} | <span className="font-medium">Bank:</span> {property.bank_details.bankName}</p>
                            </div>
                          </div>
                        )}

                        {/* Action Buttons */}
                        <div className="flex items-center space-x-3 pt-4">
                          <Button
                            onClick={() => openKycModal(property)}
                            variant="outline"
                            className="flex-1 border-purple-300 text-purple-700 hover:bg-purple-50"
                          >
                            <ShieldCheck className="h-4 w-4 mr-2" />
                            View KYC
                          </Button>
                          <Button
                            onClick={() => openPropertyPreview(property)}
                            variant="outline"
                            className="flex-1"
                            data-testid="view-property-pending-btn"
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            View Property
                          </Button>
                          <Button
                            onClick={() => handleApprove(propertyId)}
                            disabled={isProcessing}
                            className="flex-1 bg-[#7CB342] hover:bg-[#6da036] text-white"
                          >
                            <CheckCircle2 className="h-4 w-4 mr-2" />
                            {isProcessing ? 'Processing...' : 'Approve'}
                          </Button>
                          <Button
                            onClick={() => openRejectModal(propertyId)}
                            disabled={isProcessing}
                            variant="destructive"
                            className="flex-1"
                          >
                            <XCircle className="h-4 w-4 mr-2" />
                            Reject
                          </Button>
                        </div>
                      </div>
                    </div>
                    <SustainabilityReviewBlock property={property} onUpdated={fetchPendingProperties} />
                  </CardContent>
                </Card>
              );
            })}
            </div>
          )
        ) : activeTab === 'approved' ? (
          /* Approved Properties Tab */
          approvedProperties.length === 0 ? (
            <Card>
              <CardContent className="py-20 text-center">
                <CheckCircle2 className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No Approved Properties</h3>
                <p className="text-gray-600">No properties have been approved yet.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {approvedProperties.map((property) => {
                const propertyId = property._id || property.id;

                return (
                  <Card key={propertyId} className="overflow-hidden border-green-200">
                    <CardHeader className="bg-green-50 border-b border-green-200">
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-xl">{property.title}</CardTitle>
                          <p className="text-gray-600 mt-1">{property.location}, {property.country || 'India'}</p>
                        </div>
                        <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                          <CheckCircle2 className="h-3 w-3 mr-1" />
                          Approved
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="p-6">
                      <div className="flex flex-col lg:flex-row gap-6">
                        <div className="w-full lg:w-64 flex-shrink-0">
                          <img
                            src={resolveUploadUrl(property.images?.[0])}
                            alt={property.title}
                            className="w-full h-48 object-cover rounded-lg"
                            onError={(e) => {
                              e.target.src = 'https://images.unsplash.com/photo-1518780664697-55e3ad937233?w=400';
                            }}
                          />
                        </div>

                        <div className="flex-1 space-y-4">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div className="bg-gray-50 p-3 rounded-lg">
                              <p className="text-xs text-gray-500">Category</p>
                              <p className="font-semibold">{property.category}</p>
                            </div>
                            <div className="bg-gray-50 p-3 rounded-lg">
                              <p className="text-xs text-gray-500">Price/Night</p>
                              <p className="font-semibold">₹{property.price?.toLocaleString()}</p>
                            </div>
                            <div className="bg-gray-50 p-3 rounded-lg">
                              <p className="text-xs text-gray-500">Guests</p>
                              <p className="font-semibold">{property.guests}</p>
                            </div>
                            <div className="bg-gray-50 p-3 rounded-lg">
                              <p className="text-xs text-gray-500">Bedrooms</p>
                              <p className="font-semibold">{property.bedrooms}</p>
                            </div>
                          </div>

                          <div className="flex items-center justify-between pt-4 border-t">
                            <div>
                              <p className="text-sm text-gray-500">Host</p>
                              <p className="font-medium">{property.host?.name}</p>
                            </div>
                            <div className="flex items-center gap-2">
                            <Button
                              onClick={() => openPropertyPreview(property)}
                              variant="outline"
                              size="sm"
                              data-testid="view-property-approved-btn"
                            >
                              <Eye className="h-4 w-4 mr-2" />
                              View Listing
                            </Button>
                            <Button
                              onClick={() => handleRevertToPending(property)}
                              variant="outline"
                              size="sm"
                              disabled={actionLoading === propertyId}
                              className="border-yellow-400 text-yellow-700 hover:bg-yellow-50"
                              data-testid={`revert-to-pending-${propertyId}`}
                            >
                              <Clock className="h-4 w-4 mr-2" />
                              {actionLoading === propertyId ? 'Reverting…' : 'Move to Pending'}
                            </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                      <SustainabilityReviewBlock property={property} onUpdated={fetchPendingProperties} />
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )
        ) : (
          /* Rejected Properties Tab */
          rejectedProperties.length === 0 ? (
            <Card>
              <CardContent className="py-20 text-center">
                <CheckCircle2 className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No Rejected Properties</h3>
                <p className="text-gray-600">No properties have been rejected yet.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {rejectedProperties.map((property) => {
                const propertyId = property._id || property.id;

                return (
                  <Card key={propertyId} className="overflow-hidden border-red-200">
                    <CardHeader className="bg-red-50 border-b border-red-200">
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-xl">{property.title}</CardTitle>
                          <p className="text-gray-600 mt-1">{property.location}, {property.country || 'India'}</p>
                        </div>
                        <Badge className="bg-red-100 text-red-800 hover:bg-red-100">
                          <XCircle className="h-3 w-3 mr-1" />
                          Rejected
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="p-6">
                      {/* Rejection Remarks - Prominently Displayed */}
                      {property.rejection_remarks && (
                        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
                          <div className="flex items-start gap-3">
                            <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                            <div>
                              <h4 className="font-semibold text-red-800 mb-1">Rejection Reason</h4>
                              <p className="text-red-700">{property.rejection_remarks}</p>
                              {property.rejected_at && (
                                <p className="text-xs text-red-500 mt-2">
                                  Rejected on: {new Date(property.rejected_at).toLocaleDateString('en-IN', {
                                    day: 'numeric',
                                    month: 'long',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="flex flex-col lg:flex-row gap-6">
                        {/* Property Image */}
                        <div className="w-full lg:w-64 flex-shrink-0">
                          <img
                            src={resolveUploadUrl(property.images?.[0])}
                            alt={property.title}
                            className="w-full h-48 object-cover rounded-lg"
                            onError={(e) => {
                              e.target.src = 'https://images.unsplash.com/photo-1518780664697-55e3ad937233?w=400';
                            }}
                          />
                        </div>

                        {/* Property Info */}
                        <div className="flex-1 space-y-4">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div className="bg-gray-50 p-3 rounded-lg">
                              <p className="text-xs text-gray-500">Category</p>
                              <p className="font-semibold">{property.category}</p>
                            </div>
                            <div className="bg-gray-50 p-3 rounded-lg">
                              <p className="text-xs text-gray-500">Price/Night</p>
                              <p className="font-semibold">₹{property.price?.toLocaleString()}</p>
                            </div>
                            <div className="bg-gray-50 p-3 rounded-lg">
                              <p className="text-xs text-gray-500">Guests</p>
                              <p className="font-semibold">{property.guests}</p>
                            </div>
                            <div className="bg-gray-50 p-3 rounded-lg">
                              <p className="text-xs text-gray-500">Bedrooms</p>
                              <p className="font-semibold">{property.bedrooms}</p>
                            </div>
                          </div>

                          {/* Host Info */}
                          <div className="flex items-center justify-between pt-4 border-t">
                            <div>
                              <p className="text-sm text-gray-500">Host</p>
                              <p className="font-medium">{property.host?.name}</p>
                              <p className="text-sm text-gray-500">{property.hostEmail}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                onClick={() => openKycModal(property)}
                                variant="outline"
                                size="sm"
                              >
                                <ShieldCheck className="h-4 w-4 mr-2" />
                                View KYC
                              </Button>
                              <Button
                                onClick={() => handleRevertToPending(property)}
                                variant="outline"
                                size="sm"
                                disabled={actionLoading === propertyId}
                                className="border-yellow-400 text-yellow-700 hover:bg-yellow-50"
                                data-testid={`revert-to-pending-rejected-${propertyId}`}
                              >
                                <Clock className="h-4 w-4 mr-2" />
                                {actionLoading === propertyId ? 'Reverting…' : 'Move to Pending'}
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )
        )}

        {/* KYC Documents Modal */}
        <Dialog open={kycModalOpen} onOpenChange={setKycModalOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-purple-600" />
                KYC Documents - {selectedPropertyKyc?.title}
              </DialogTitle>
            </DialogHeader>
            
            {selectedPropertyKyc && (
              <div className="space-y-6 pt-4">
                {/* Host Information */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">Host Information</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Name:</span>
                      <span className="ml-2 font-medium">{selectedPropertyKyc.host?.name}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Email:</span>
                      <span className="ml-2 font-medium">{selectedPropertyKyc.hostEmail}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Phone:</span>
                      <span className="ml-2 font-medium">{selectedPropertyKyc.hostPhone}</span>
                    </div>
                  </div>
                </div>

                {/* KYC Documents */}
                <div>
                  <h3 className="font-semibold text-gray-900 mb-4">Uploaded Documents</h3>
                  
                  {selectedPropertyKyc.kyc_documents && Object.keys(selectedPropertyKyc.kyc_documents).length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {selectedPropertyKyc.kyc_documents.hostPhoto && (
                        <div className="border rounded-lg overflow-hidden">
                          <div className="bg-blue-50 px-4 py-2 flex items-center">
                            <User className="h-4 w-4 text-blue-700 mr-2" />
                            <span className="font-medium text-blue-700">Host Photo</span>
                          </div>
                          <div className="p-4">
                            <img 
                              src={resolveUploadUrl(selectedPropertyKyc.kyc_documents.hostPhoto)}
                              alt="Host Photo"
                              className="w-full h-48 object-contain bg-gray-100 rounded"
                              onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                            />
                            <div className="hidden w-full h-48 flex-col items-center justify-center bg-gray-100 rounded text-gray-400">
                              <User className="h-10 w-10 mb-2" />
                              <span className="text-sm font-medium">Document not found</span>
                              <span className="text-xs mt-1">File may have been removed</span>
                            </div>
                            <a 
                              href={resolveUploadUrl(selectedPropertyKyc.kyc_documents.hostPhoto)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mt-2 text-sm text-blue-600 hover:underline flex items-center justify-center"
                            >
                              <Eye className="h-3 w-3 mr-1" /> Open in new tab
                            </a>
                          </div>
                        </div>
                      )}
                      
                      {selectedPropertyKyc.kyc_documents.aadhaarCard && (
                        <div className="border rounded-lg overflow-hidden">
                          <div className="bg-green-50 px-4 py-2 flex items-center">
                            <FileText className="h-4 w-4 text-green-700 mr-2" />
                            <span className="font-medium text-green-700">Aadhaar Card</span>
                          </div>
                          <div className="p-4">
                            <img 
                              src={resolveUploadUrl(selectedPropertyKyc.kyc_documents.aadhaarCard)}
                              alt="Aadhaar Card"
                              className="w-full h-48 object-contain bg-gray-100 rounded"
                              onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                            />
                            <div className="hidden w-full h-48 flex-col items-center justify-center bg-gray-100 rounded text-gray-400">
                              <FileText className="h-10 w-10 mb-2" />
                              <span className="text-sm font-medium">Document not found</span>
                              <span className="text-xs mt-1">File may have been removed</span>
                            </div>
                            <a 
                              href={resolveUploadUrl(selectedPropertyKyc.kyc_documents.aadhaarCard)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mt-2 text-sm text-green-600 hover:underline flex items-center justify-center"
                            >
                              <Eye className="h-3 w-3 mr-1" /> Open in new tab
                            </a>
                          </div>
                        </div>
                      )}
                      
                      {selectedPropertyKyc.kyc_documents.panCard && (
                        <div className="border rounded-lg overflow-hidden">
                          <div className="bg-orange-50 px-4 py-2 flex items-center">
                            <CreditCard className="h-4 w-4 text-orange-700 mr-2" />
                            <span className="font-medium text-orange-700">PAN Card</span>
                          </div>
                          <div className="p-4">
                            <img 
                              src={resolveUploadUrl(selectedPropertyKyc.kyc_documents.panCard)}
                              alt="PAN Card"
                              className="w-full h-48 object-contain bg-gray-100 rounded"
                              onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                            />
                            <div className="hidden w-full h-48 flex-col items-center justify-center bg-gray-100 rounded text-gray-400">
                              <CreditCard className="h-10 w-10 mb-2" />
                              <span className="text-sm font-medium">Document not found</span>
                              <span className="text-xs mt-1">File may have been removed</span>
                            </div>
                            <a 
                              href={resolveUploadUrl(selectedPropertyKyc.kyc_documents.panCard)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mt-2 text-sm text-orange-600 hover:underline flex items-center justify-center"
                            >
                              <Eye className="h-3 w-3 mr-1" /> Open in new tab
                            </a>
                          </div>
                        </div>
                      )}
                      
                      {selectedPropertyKyc.kyc_documents.bankCheque && (
                        <div className="border rounded-lg overflow-hidden">
                          <div className="bg-purple-50 px-4 py-2 flex items-center">
                            <Building2 className="h-4 w-4 text-purple-700 mr-2" />
                            <span className="font-medium text-purple-700">Bank Document</span>
                          </div>
                          <div className="p-4">
                            <img 
                              src={resolveUploadUrl(selectedPropertyKyc.kyc_documents.bankCheque)}
                              alt="Bank Document"
                              className="w-full h-48 object-contain bg-gray-100 rounded"
                              onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                            />
                            <div className="hidden w-full h-48 flex-col items-center justify-center bg-gray-100 rounded text-gray-400">
                              <Building2 className="h-10 w-10 mb-2" />
                              <span className="text-sm font-medium">Document not found</span>
                              <span className="text-xs mt-1">File may have been removed</span>
                            </div>
                            <a 
                              href={resolveUploadUrl(selectedPropertyKyc.kyc_documents.bankCheque)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mt-2 text-sm text-purple-600 hover:underline flex items-center justify-center"
                            >
                              <Eye className="h-3 w-3 mr-1" /> Open in new tab
                            </a>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-8 bg-gray-50 rounded-lg">
                      <FileText className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                      <p className="text-gray-500">No KYC documents uploaded</p>
                    </div>
                  )}
                </div>

                {/* PAN Details */}
                {selectedPropertyKyc.pan_details && selectedPropertyKyc.pan_details.panNumber && (
                  <div className="bg-orange-50 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-3">PAN Card Details</h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">PAN Number:</span>
                        <span className="ml-2 font-medium font-mono">{selectedPropertyKyc.pan_details.panNumber}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Name on PAN:</span>
                        <span className="ml-2 font-medium">{selectedPropertyKyc.pan_details.panHolderName}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Bank Details */}
                {selectedPropertyKyc.bank_details && selectedPropertyKyc.bank_details.accountName && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-3">Bank Account Details</h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Account Holder:</span>
                        <span className="ml-2 font-medium">{selectedPropertyKyc.bank_details.accountName}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Account Number:</span>
                        <span className="ml-2 font-medium">{selectedPropertyKyc.bank_details.accountNumber}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">IFSC Code:</span>
                        <span className="ml-2 font-medium">{selectedPropertyKyc.bank_details.ifscCode}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Bank Name:</span>
                        <span className="ml-2 font-medium">{selectedPropertyKyc.bank_details.bankName}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Property Preview Modal */}
        <PropertyPreviewDialog
          open={previewOpen}
          onOpenChange={setPreviewOpen}
          propertyId={previewProperty ? (previewProperty._id || previewProperty.id) : null}
          propertySlug={previewProperty?.slug}
          propertyTitle={previewProperty?.title}
        />

        {/* Reject Property Modal */}
        <Dialog open={rejectModalOpen} onOpenChange={setRejectModalOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="h-5 w-5" />
                Reject Property
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-800">
                  Please provide a reason for rejecting this property. This will be sent to the host.
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="rejectRemarks" className="text-sm font-medium">
                  Rejection Remarks <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  id="rejectRemarks"
                  placeholder="Enter the reason for rejection (e.g., incomplete KYC documents, unclear photos, incorrect information...)"
                  value={rejectRemarks}
                  onChange={(e) => setRejectRemarks(e.target.value)}
                  className="min-h-[120px]"
                />
                <p className="text-xs text-gray-500">
                  {rejectRemarks.length}/500 characters
                </p>
              </div>
            </div>

            <DialogFooter className="flex gap-3 sm:gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setRejectModalOpen(false);
                  setRejectRemarks('');
                  setRejectPropertyId(null);
                }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleReject}
                disabled={!rejectRemarks.trim() || actionLoading}
                variant="destructive"
                className="flex-1"
              >
                {actionLoading ? 'Rejecting...' : 'Confirm Rejection'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminApprovalPage;
